export const businessTypes = [
  {
    slug: 'restaurant-pos',
    path: '/restaurant-pos',
    title: 'Restaurant POS System',
    cardTitle: 'Full Service',
    seoTitle: 'POS System for Restaurant | Restaurant Point of Sale Systems',
    seoDescription:
      'Restaurant point of sale systems for full-service dining. Tillio is a cloud POS system for restaurants—tables, orders, payments, teams, and kitchen tickets.',
    images: {
      hero: '/hero-restaurant.webp',
      card: '/business-full-service.webp',
      section1: '/feature-tables.webp', // Service that stays organized
      section2: '/feature-analytics.webp', // Managers get the full picture
    },
    intro:
      'Looking for a POS system for restaurants? Run the dining room and back office from one cloud point of sale—tables, tickets, payments, and staff included.',
    sections: [
      {
        title: 'Service that stays organized',
        description:
          'From seating to dessert, keep every party’s order clear for servers, managers, and the kitchen.',
        bullets: [
          'Table status and open checks',
          'Course-friendly order flow',
          'Split checks without slowing service',
          'Kitchen display or printer routing',
        ],
      },
      {
        title: 'Managers get the full picture',
        description:
          'Track sales, labor signals, and menu performance without logging into five different tools.',
        bullets: [
          'Real-time sales visibility',
          'Team roles and PIN access',
          'Menu updates that sync instantly',
          'Stripe payments built into checkout',
        ],
      },
    ],
    relatedFeatures: ['point-of-sale', 'table-management', 'customer-facing-display'],
  },
  {
    slug: 'cafe-pos',
    path: '/cafe-pos',
    title: 'Café & Coffee Shop POS',
    cardTitle: 'Coffee & Tea',
    seoTitle: 'Café & Coffee Shop POS System',
    seoDescription:
      'Speed through morning rushes with Tillio POS—fast ordering, card payments, and menu tools for cafés and coffee shops.',
    images: {
      hero: '/hero-cafe.webp',
      card: '/business-coffee.webp',
      section1: '/feature-pos.webp', // Built for the rush
      section2: '/feature-menu.webp', // Menus that match the day
    },
    intro:
      'Keep the line moving with a POS that handles modifiers, quick payments, and a menu that changes with the daypart.',
    sections: [
      {
        title: 'Built for the rush',
        description:
          'Ring up drinks and pastries fast, customize orders, and close tabs before the next guest steps up.',
        bullets: [
          'Quick item entry and modifiers',
          'Contactless and card payments',
          'Counter-friendly workflows',
          'Clear tickets for baristas',
        ],
      },
      {
        title: 'Menus that match the day',
        description:
          'Swap seasonal drinks, 86 pastries, and keep pricing accurate across every tablet.',
        bullets: [
          'Real-time menu updates',
          'Categories for drinks and food',
          'Sales insights by item',
          'Staff PIN login for shifts',
        ],
      },
    ],
    relatedFeatures: ['point-of-sale', 'payments', 'customer-facing-display'],
  },
  {
    slug: 'bar-pos',
    path: '/bar-pos',
    title: 'Bar & Club POS',
    cardTitle: 'Bar & Club',
    seoTitle: 'Bar POS System for Bars & Clubs',
    seoDescription:
      'Run a faster bar with Tillio POS—tabs, payments, team access, and order flow designed for nightlife service.',
    images: {
      hero: '/hero-bar.webp',
      card: '/business-bar.webp',
      section1: '/feature-payments.webp', // Tabs without the tangle
      section2: '/feature-team.webp', // Staff access you can trust
    },
    intro:
      'Keep tabs moving, drinks accurate, and staff accountable when the room is loud and the night is busy.',
    sections: [
      {
        title: 'Tabs without the tangle',
        description:
          'Open and close checks quickly, apply modifiers, and process payments without leaving the bar.',
        bullets: [
          'Fast checkout for walk-ups',
          'Secure Stripe card payments',
          'Clear item and modifier entry',
          'Manager controls when you need them',
        ],
      },
      {
        title: 'Staff access you can trust',
        description:
          'PIN-based login and roles keep the register locked down while still feeling fast for bartenders.',
        bullets: [
          'Unique staff PINs',
          'Role-based permissions',
          'Time tracking for shifts',
          'Sales visibility for owners',
        ],
      },
    ],
    relatedFeatures: ['payments', 'team-management', 'point-of-sale'],
  },
  {
    slug: 'bakery-pos',
    path: '/bakery-pos',
    title: 'Bakery POS',
    cardTitle: 'Bakery',
    seoTitle: 'Bakery POS System',
    seoDescription:
      'Sell pastries and custom orders faster with Tillio POS menu tools, payments, and simple counter workflows for bakeries.',
    images: {
      hero: '/hero-bakery.webp',
      card: '/business-bakery.webp',
      section1: '/feature-menu.webp', // Counter service that stays clear
      section2: '/feature-analytics.webp', // Know what sold today
    },
    intro:
      'From morning pastry runs to custom cake pickups, keep inventory of what’s sellable and checkout simple.',
    sections: [
      {
        title: 'Counter service that stays clear',
        description:
          'Organize categories for breads, pastries, and specialty orders so staff can find items instantly.',
        bullets: [
          'Menu categories built for bakery assortments',
          'Quick availability updates when items sell out',
          'Card payments at the counter',
          'Receipt options for pickups and wholesale',
        ],
      },
      {
        title: 'Know what sold today',
        description:
          'See bestsellers and quiet hours so you bake and staff for demand—not guesswork.',
        bullets: [
          'Item-level sales insight',
          'Daypart-friendly reporting',
          'Multi-device checkout',
          'Simple onboarding for seasonal staff',
        ],
      },
    ],
    relatedFeatures: ['menu-management', 'payments', 'analytics'],
  },
  {
    slug: 'fast-casual-pos',
    path: '/fast-casual-pos',
    title: 'Fast Casual POS',
    cardTitle: 'Fast Casual',
    seoTitle: 'Fast Casual Restaurant POS System',
    seoDescription:
      'Speed up lines and kitchen handoffs with Tillio POS—built for fast-casual ordering, payments, and KDS workflows.',
    images: {
      hero: '/hero-fast-casual.webp',
      card: '/business-fast-casual.webp',
      section1: '/showcase-checkout.webp', // Order. Pay. Fire. Repeat.
      section2: '/feature-kds.webp', // Kitchen clarity at pace
    },
    intro:
      'High volume, short tickets, and a kitchen that needs clarity. Tillio keeps the line and the make station aligned.',
    sections: [
      {
        title: 'Order. Pay. Fire. Repeat.',
        description:
          'Move guests through the counter with fast item entry and payments that don’t create a second queue.',
        bullets: [
          'Rapid POS entry for bowls, builds, and combos',
          'Modifiers without slowing the cashier',
          'Stripe Terminal for chip and tap',
          'Tickets that reach the line immediately',
        ],
      },
      {
        title: 'Kitchen clarity at pace',
        description:
          'Pair the POS with kitchen display so expo isn’t decoding scribbled tickets mid-rush.',
        bullets: [
          'Digital ticket routing',
          'Consistent modifier display',
          'Fewer remakes and missing items',
          'Analytics to tune the menu',
        ],
      },
    ],
    relatedFeatures: ['point-of-sale', 'kitchen-display', 'payments'],
  },
  {
    slug: 'fine-dining-pos',
    path: '/fine-dining-pos',
    title: 'Fine Dining POS',
    cardTitle: 'Fine Dining',
    seoTitle: 'Fine Dining Restaurant POS System',
    seoDescription:
      'Deliver polished service with Tillio POS—table management, course-friendly orders, payments, and discreet staff tools.',
    images: {
      hero: '/hero-fine-dining.webp',
      card: '/business-fine-dining.webp',
      section1: '/feature-tables.webp', // Tables and timing, handled
      section2: '/feature-team.webp', // Control without friction
    },
    intro:
      'Support a refined guest experience with tools that stay out of the way—while keeping the floor and kitchen precise.',
    sections: [
      {
        title: 'Tables and timing, handled',
        description:
          'Track parties, courses, and checks so service feels intentional—not rushed or chaotic.',
        bullets: [
          'Table-centric order management',
          'Notes and modifiers for special requests',
          'Discreet payment workflows',
          'Kitchen coordination without paper clutter',
        ],
      },
      {
        title: 'Control without friction',
        description:
          'Managers get oversight; servers get speed. Roles and reporting keep the room running to standard.',
        bullets: [
          'PIN access by role',
          'Sales and performance reporting',
          'Menu precision across devices',
          'Hardware that fits a polished station',
        ],
      },
    ],
    relatedFeatures: ['table-management', 'point-of-sale', 'team-management'],
  },
]

export function getBusinessType(slug) {
  return businessTypes.find((b) => b.slug === slug)
}
